import Vue from 'vue';
import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import ContactAPI from '../../../api/contacts';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
} from './helpers/actionHelpers';
import {
  listKeysForNewConversation,
  mergeMessagesById,
} from './helpers/conversationLists';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import { toEpochSeconds } from 'dashboard/mixins/conversations';

const MESSAGES_PAGE_SIZE = 20;
const MESSAGES_FETCH_TIMEOUT_MS = 30000;

function withTimeout(promise, timeoutMs = MESSAGES_FETCH_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('messages_fetch_timeout')), timeoutMs);
    }),
  ]);
}

function parseMessagesResponse(res) {
  const body = res.data || {};
  const meta = body.meta || {};
  let payload = body.payload;
  if (
    !Array.isArray(payload) &&
    body.data &&
    Array.isArray(body.data.payload)
  ) {
    payload = body.data.payload;
  }
  payload = Array.isArray(payload) ? payload : [];
  return { meta, payload };
}

function newMessagesForChat(chat, payload) {
  const existingIds = new Set(
    (chat?.messages || [])
      .map(message => Number(message.id))
      .filter(Number.isFinite)
  );
  return payload.filter(
    message =>
      Number.isFinite(Number(message.id)) &&
      !existingIds.has(Number(message.id))
  );
}

function shouldMarkAllMessagesLoaded(payload, addedMessages) {
  if (!payload.length) return true;
  if (payload.length && !addedMessages.length) return true;
  return payload.length < MESSAGES_PAGE_SIZE;
}

// actions
const actions = {
  getConversation: async ({ commit, rootGetters }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, {
        conversation: response.data,
        currentUserId: rootGetters.getCurrentUser?.id,
      });
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  /**
   * Loads a contact's conversations into the main conversation lists so
   * read/unread and ActionCable updates apply to the same objects as the inbox.
   */
  fetchContactConversations: async ({ commit, rootGetters }, contactId) => {
    if (
      contactId === undefined ||
      contactId === null ||
      contactId === '' ||
      Number.isNaN(Number(contactId))
    ) {
      return;
    }
    commit(types.SET_CONTACT_SIDEBAR_UI_FLAG, {
      isFetching: true,
      fetchError: null,
    });
    try {
      const response = await ContactAPI.getConversations(contactId);
      const list = response.data.payload || [];
      const currentUserId = rootGetters.getCurrentUser?.id;
      list.forEach(conversation => {
        commit(types.UPDATE_CONVERSATION, { conversation, currentUserId });
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchContactConversations', error);
      commit(types.SET_CONTACT_SIDEBAR_UI_FLAG, { fetchError: true });
      if (typeof window !== 'undefined' && window.bus) {
        window.bus.$emit(
          'newToastMessage',
          'Could not load conversations for this contact. Try again.'
        );
      }
    } finally {
      commit(types.SET_CONTACT_SIDEBAR_UI_FLAG, { isFetching: false });
    }
  },

  /**
   * Creates a conversation from the contact / new-conversation panel and merges
   * the result into the main conversation store.
   */
  createConversationFromContact: async (
    { commit, dispatch, rootGetters },
    params
  ) => {
    const {
      inboxId,
      message,
      contactId,
      sourceId,
      mailSubject,
      assigneeId,
      files,
      onUploadProgress,
    } = params;
    commit(types.SET_CONTACT_SIDEBAR_UI_FLAG, { isCreating: true });
    try {
      let payload;
      if (files && files.length > 0) {
        payload = new FormData();
        payload.append('inbox_id', inboxId);
        payload.append('contact_id', contactId);
        if (sourceId) {
          payload.append('source_id', sourceId);
        }
        if (mailSubject) {
          payload.append('additional_attributes[mail_subject]', mailSubject);
        }
        payload.append('assignee_id', assigneeId);
        if (message && message.content) {
          payload.append('message[content]', message.content);
        }
        if (message && message.cc_emails) {
          payload.append('message[cc_emails]', message.cc_emails);
        }
        if (message && message.bcc_emails) {
          payload.append('message[bcc_emails]', message.bcc_emails);
        }
        files.forEach(file => {
          payload.append('message[attachments][]', file);
        });
      } else {
        payload = {
          inbox_id: inboxId,
          contact_id: contactId,
          source_id: sourceId,
          additional_attributes: {
            mail_subject: mailSubject,
          },
          message,
          assignee_id: assigneeId,
        };
      }

      const requestConfig =
        files && files.length > 0 && onUploadProgress
          ? { onUploadProgress }
          : undefined;
      const { data } = await ConversationApi.create(payload, requestConfig);
      const currentUserId = rootGetters.getCurrentUser?.id;
      commit(types.UPDATE_CONVERSATION, { conversation: data, currentUserId });
      dispatch('conversationLabels/setConversationLabel', {
        id: data.id,
        data: data.labels || [],
      });
      if (data.meta?.sender) {
        dispatch('contacts/setContact', data.meta.sender);
      }
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_SIDEBAR_UI_FLAG, { isCreating: false });
    }
  },

  fetchAllConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

   fetchConversationsForInboxes: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      return data;
    } catch (error) {
      // Handle error
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
    commit(types.CLEAR_DETACHED_LIST_HIGHLIGHT);
  },

  setVisibleConversationIds({ commit }, ids) {
    commit(types.SET_VISIBLE_CONVERSATION_IDS, ids);
  },

  setDetachedListHighlight({ commit }, payload) {
    commit(types.SET_DETACHED_LIST_HIGHLIGHT, payload);
  },

  clearDetachedListHighlight({ commit }) {
    commit(types.CLEAR_DETACHED_LIST_HIGHLIGHT);
  },

  fetchPreviousMessages: async ({ commit, getters }, data) => {
    const conversationId = data.conversationId;
    try {
      const res = await withTimeout(
        MessageApi.getPreviousMessages(data),
        MESSAGES_FETCH_TIMEOUT_MS
      );
      const { meta, payload } = parseMessagesResponse(res);
      const chat = getters.getConversationById(conversationId);
      const addedMessages = newMessagesForChat(chat, payload);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: conversationId,
        data: payload,
      });
      if (shouldMarkAllMessagesLoaded(payload, addedMessages)) {
        commit(types.SET_ALL_MESSAGES_LOADED, conversationId);
      }
    } catch (error) {
      // Stop infinite scroll retries when the API fails or times out.
      commit(types.SET_ALL_MESSAGES_LOADED, conversationId);
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    if (
      conversationId === undefined ||
      conversationId === null ||
      conversationId === '' ||
      Number.isNaN(Number(conversationId))
    ) {
      return;
    }
    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: data.payload,
      });
    } catch (error) {
      // Handle error
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch, getters },
    { conversationId }
  ) => {
    const { syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = getters.getConversationById(conversationId);
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const res = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      const body = res.data || {};
      const meta = body.meta || {};
      let payload = body.payload;
      if (!Array.isArray(payload) && body.data && Array.isArray(body.data.payload)) {
        payload = body.data.payload;
      }
      payload = Array.isArray(payload) ? payload : [];
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message =>
          !messages.find(item => Number(item.id) === Number(message.id))
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, getters },
    { conversationId }
  ) => {
    const selectedChat = getters.getConversationById(conversationId);
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch, getters, rootGetters }, { data, after }) {
    if (!data || data.id == null) {
      return;
    }
    commit(types.UPDATE_CONVERSATION, {
      conversation: data,
      currentUserId: rootGetters.getCurrentUser?.id,
    });
    const activeChat = getters.getConversationById(data.id) || data;
    commit(types.SET_CURRENT_CHAT_WINDOW, activeChat);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, activeChat.id);
    if (activeChat.dataFetched !== true) {
      try {
        const targetId = Number(after);
        if (after && Number.isFinite(targetId)) {
          const msgs = activeChat.messages || [];
          const ids = msgs.map(m => Number(m.id)).filter(Number.isFinite);
          let beforeId = ids.length ? Math.max(...ids) : undefined;
          if (!Number.isFinite(beforeId) || beforeId <= targetId) {
            beforeId = targetId + 1;
          }

          const res = await withTimeout(
            MessageApi.getPreviousMessages({
              conversationId: activeChat.id,
              after: targetId,
              before: beforeId,
            }),
            MESSAGES_FETCH_TIMEOUT_MS
          );
          const body = res.data || {};
          const meta = body.meta || {};
          let payload = body.payload;
          if (
            !Array.isArray(payload) &&
            body.data &&
            Array.isArray(body.data.payload)
          ) {
            payload = body.data.payload;
          }
          payload = Array.isArray(payload) ? payload : [];
          commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
            id: activeChat.id,
            data: meta,
          });
          const merged = mergeMessagesById(
            activeChat.messages || [],
            payload
          ).sort(
            (a, b) =>
              toEpochSeconds(a.created_at) - toEpochSeconds(b.created_at)
          );
          commit(types.SET_MISSING_MESSAGES, {
            id: activeChat.id,
            data: merged.length ? merged : activeChat.messages || [],
          });
          if (!payload.length && !(activeChat.messages || []).length) {
            commit(types.SET_ALL_MESSAGES_LOADED, activeChat.id);
          }
        } else {
          const res = await withTimeout(
            MessageApi.getPreviousMessages({
              conversationId: activeChat.id,
            }),
            MESSAGES_FETCH_TIMEOUT_MS
          );
          const body = res.data || {};
          const meta = body.meta || {};
          let payload = body.payload;
          if (
            !Array.isArray(payload) &&
            body.data &&
            Array.isArray(body.data.payload)
          ) {
            payload = body.data.payload;
          }
          payload = Array.isArray(payload) ? payload : [];
          commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
            id: activeChat.id,
            data: meta,
          });
          // List API messages lack attachments; messages API is authoritative.
          const merged = mergeMessagesById(
            activeChat.messages || [],
            payload
          ).sort(
            (a, b) =>
              toEpochSeconds(a.created_at) - toEpochSeconds(b.created_at)
          );
          commit(types.SET_MISSING_MESSAGES, {
            id: activeChat.id,
            data: merged.length ? merged : activeChat.messages || [],
          });
          if (
            (!payload.length && !(activeChat.messages || []).length) ||
            payload.length < MESSAGES_PAGE_SIZE
          ) {
            commit(types.SET_ALL_MESSAGES_LOADED, activeChat.id);
          }
        }
      } catch (error) {
        // Ignore error
      } finally {
        Vue.set(activeChat, 'dataFetched', true);
      }
    }
  },

  assignAgent: async ({ commit, rootGetters }, { conversationId, agentId }) => {
    const response = await ConversationApi.assignAgent({
      conversationId,
      agentId,
    });
    const assignee =
      response.data === '' ||
      response.data === undefined ||
      response.data === null
        ? null
        : response.data;
    commit(types.ASSIGN_AGENT, {
      assignee,
      currentUserId: rootGetters.getCurrentUser?.id,
    });
    return response;
  },

  setCurrentChatAssignee({ commit, rootGetters }, assignee) {
    commit(types.ASSIGN_AGENT, {
      assignee,
      currentUserId: rootGetters.getCurrentUser?.id,
    });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, data) => {
    // Upload callbacks belong to the composer, so they are kept out of the
    // message stored for display and retry.
    const { onUploadProgress, onUploadSettled, ...pendingMessage } = data;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = await MessageApi.create(pendingMessage, {
        onUploadProgress,
      });
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    } finally {
      if (typeof onUploadSettled === 'function') onUploadSettled();
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState, rootGetters }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;

    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, {
        conversation,
        listKeys: listKeysForNewConversation(
          conversation,
          rootGetters.getCurrentUser?.id
        ),
        currentUserId: rootGetters.getCurrentUser?.id,
      });
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, rootGetters }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, {
      conversation,
      currentUserId: rootGetters.getCurrentUser?.id,
    });

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit, rootGetters }, data) {
    commit(types.UPDATE_ASSIGNEE, {
      ...data,
      currentUserId: rootGetters.getCurrentUser?.id,
    });
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },
  searchConversation: async({commit},{q})=>{
    try{
      commit(types.SET_LIST_LOADING_STATUS);
      const response=await ConversationApi.searchfilter({q});
      const {
        data: { payload },
      }=response.data;
      return payload;
    }catch(err){
      console.error("Error while Fetching the data");
    }
    finally{
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  syncRecording: async (_, { conversationId, messageId }) => {
    try {
      const response = await ConversationApi.syncRecording({
        conversationId,
        messageId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to sync recording');
    }
  },

  syncSummary: async (_, { conversationId, messageId }) => {
    try {
      const response = await ConversationApi.syncSummary({
        conversationId,
        messageId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to sync summary');
    }
  },

  fetchCallTranscription: async (_, { conversationId, messageId }) => {
    try {
      const response = await ConversationApi.getCallTranscription({
        conversationId,
        messageId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to fetch transcription'
      );
    }
  },

  toggleBot: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.toggleBot(conversationId);
      if (response.data.success) {
        commit(types.UPDATE_CONVERSATION_BOT_STATUS, {
          conversationId,
          botDisabled: response.data.bot_disabled,
        });
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to toggle bot');
    }
  },

  generateAiContent: async (_, conversationId, options = {}) => {
    const response = await ConversationApi.generateAiContent(conversationId, options);
    return response.data;
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
